import { Component, EventBus } from '../core/smpl-components.js'
import { Events } from '../core/events'

import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

// -------------------------------------------------------------------------- //
// Rolls the home header up and off the screen while the intro is held still at
// the top, uncovering it from behind. Once the header has fully left the top,
// the intro is released and scrolls on with the rest of the page.
//
// The intro is wrapped so the *overlap offset* lives on the wrapper while the
// pin is applied to the inner intro. If the offset lived on the pinned element
// itself, ScrollTrigger's pin-spacer would capture it below the fold and the
// reveal would show blank space before the intro scrolled into view.
export default class extends Component {
	prepare() {
		this.elHomeIntro = this.element
		this.elHomeHeader = this.element.parentElement.querySelector('.home-header')
	}

	mount() {
		if (!this.elHomeHeader) return

		// Wrap the intro so the negative offset sits on the wrapper, not on the
		// element we hand to ScrollTrigger's pin.
		this.wrapper = document.createElement('div')
		this.wrapper.className = 'home-intro-reveal'
		this.elHomeIntro.parentNode.insertBefore(this.wrapper, this.elHomeIntro)
		this.wrapper.appendChild(this.elHomeIntro)

		// Hand the scallop STACKING (z-index) to the wrapper. The pin below writes
		// a transform onto the intro — that spawns a stacking context and, worse,
		// GSAP restores a style snapshot on every refresh, wiping any z-index the
		// scallop stacker put on the intro. On Swup that snapshot is captured
		// before the stacker runs, so the intro ends up with no z-index and the
		// next layer paints over its downward bump. The wrapper is never touched
		// by GSAP, so the layer order survives there. The intro keeps its
		// `scallop-edge--bottom` class (the visual bump); only the layer moves.
		this.elHomeIntro.removeAttribute('data-scallop-layer')
		this.wrapper.setAttribute('data-scallop-layer', '')
		EventBus.emit(Events.SCALLOP_RESTACK, {})

		// The header has to sit *above* the intro so it can roll off and uncover
		// it. Both need a positioned context for the z-index to take effect.
		gsap.set(this.elHomeHeader, { position: 'relative' })
		gsap.set(this.wrapper, { position: 'relative' })

		// Pull the wrapper up by the header's height so the intro starts out
		// directly behind the header (they overlap at the top of the page). The
		// pin's spacing adds this scroll distance back, so content below doesn't
		// jump. Re-measured on every refresh to track the responsive header.
		const setOverlap = () => {
			gsap.set(this.wrapper, { marginTop: -this.elHomeHeader.offsetHeight })
		}

		this.trigger = ScrollTrigger.create({
			trigger: this.wrapper,
			start: 'top top', // wrapper (== intro) is at the top: reveal begins
			end: () => '+=' + this.elHomeHeader.offsetHeight, // for the header's height
			scrub: true,
			pin: this.elHomeIntro, // hold the intro at the top while the header rolls off
			pinSpacing: true,
			invalidateOnRefresh: true,
			onRefreshInit: setOverlap,
		})

		setOverlap()
		ScrollTrigger.refresh()
	}

	unmount() {
		if (this.trigger) {
			this.trigger.kill()
			this.trigger = null
		}
		gsap.set(this.elHomeIntro, { clearProps: 'position' })
		gsap.set(this.elHomeHeader, { clearProps: 'position' })

		// Give the scallop layer back to the intro and unwrap it so the DOM is
		// left as we found it.
		this.elHomeIntro.setAttribute('data-scallop-layer', '')
		if (this.wrapper && this.wrapper.parentNode) {
			this.wrapper.parentNode.insertBefore(this.elHomeIntro, this.wrapper)
			this.wrapper.remove()
			this.wrapper = null
		}
		EventBus.emit(Events.SCALLOP_RESTACK, {})
	}
}
